/**
 * Resume Studio — client-side session persistence.
 *
 * We save just enough metadata so the user can pick up where they left
 * off after closing the tab. The full session lives on the server (7-day
 * TTL); this is only the pointer + display info for the resume banner.
 *
 * Key = `naukridaddy.resume_studio.last`.
 * Value = { session_id, email, name, target_role, phase, tier,
 *           payment_status, updated_at }.
 * TTL client-side: 7 days (matches server session TTL).
 */

const KEY = "naukridaddy.resume_studio.last";
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export function saveLastSession(session, extra = {}) {
    if (!session || !session.session_id) return;
    try {
        const payload = {
            session_id: session.session_id,
            email: session.email || "",
            name: extra.name || session.name || "",
            target_role: session.target_role || "",
            phase: extra.phase || "preview",
            tier: session.tier || "",
            payment_status: session.payment_status || "pending",
            updated_at: new Date().toISOString(),
        };
        localStorage.setItem(KEY, JSON.stringify(payload));
    } catch { /* localStorage may be disabled — silently skip */ }
}

export function loadLastSession() {
    try {
        const raw = localStorage.getItem(KEY);
        if (!raw) return null;
        const payload = JSON.parse(raw);
        const ts = Date.parse(payload.updated_at);
        if (!Number.isFinite(ts) || Date.now() - ts > MAX_AGE_MS) {
            localStorage.removeItem(KEY);
            return null;
        }
        return payload;
    } catch {
        return null;
    }
}

export function clearLastSession() {
    try { localStorage.removeItem(KEY); } catch { /* noop */ }
}

/** Human-readable "how long ago" for the banner. */
export function relativeTime(iso) {
    const t = Date.parse(iso);
    if (!Number.isFinite(t)) return "";
    const diff = Date.now() - t;
    const mins = Math.round(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs} hour${hrs === 1 ? "" : "s"} ago`;
    const days = Math.round(hrs / 24);
    return `${days} day${days === 1 ? "" : "s"} ago`;
}
